/**
 * ChatComposer — bottom input strip for the simulador chat.
 *
 * Multiline textarea (auto-grow up to 6 rows). Enter sends, Shift+Enter newline.
 * Mic toggles audio recording → Whisper transcription fills the textarea (user
 * reviews and edits before sending). Hint button asks the coaching model for a
 * single-line tip; cumulative penalty is shown beside the icon.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  Microphone,
  MicrophoneSlash,
  CircleNotch,
  Lightbulb,
  PaperPlaneRight,
  Stop,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { composerMotion } from "../lib/chatMotion";
import { useSimulatorAudio } from "../hooks/useSimulatorAudio";
import { useHasTranscriptionKey } from "@/features/byok/hooks/useHasTranscriptionKey";
import { TranscriptionKeyGate } from "@/features/byok/components/TranscriptionKeyGate";

export interface ChatComposerProps {
  onSend: (content: string) => void;
  onRequestHint?: () => void;
  hintLoading?: boolean;
  hintPenaltyTotal?: number;
  disabled?: boolean;
  isReplying?: boolean;
}

const MAX_ROWS = 6;
const LINE_HEIGHT_PX = 22;

export function ChatComposer({
  onSend,
  onRequestHint,
  hintLoading,
  hintPenaltyTotal,
  disabled,
  isReplying,
}: ChatComposerProps) {
  const [value, setValue] = useState("");
  const [showKeyModal, setShowKeyModal] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const audio = useSimulatorAudio();
  const transcriptionKey = useHasTranscriptionKey();

  // Auto-grow up to MAX_ROWS.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = MAX_ROWS * LINE_HEIGHT_PX + 16;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [value]);

  const isDisabled = disabled || isReplying;
  const canSend = value.trim().length > 0 && !isDisabled;

  const submit = () => {
    if (!canSend) return;
    const content = value.trim();
    setValue("");
    onSend(content);
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const handleMicClick = async () => {
    // Pre-flight: block recording entirely if user has no transcription key.
    // Admins always pass (handled by useHasTranscriptionKey).
    if (audio.phase === "idle" && !transcriptionKey.canTranscribe && !transcriptionKey.isLoading) {
      setShowKeyModal(true);
      return;
    }
    if (audio.phase === "idle") {
      await audio.start();
      return;
    }
    if (audio.phase === "recording") {
      const transcript = await audio.stopAndTranscribe();
      if (transcript) {
        setValue((prev) => (prev ? `${prev.trimEnd()} ${transcript}` : transcript));
        // Focus the textarea so the user can edit.
        requestAnimationFrame(() => taRef.current?.focus());
      }
    }
  };

  const micIcon = (() => {
    if (!audio.isSupported) return <MicrophoneSlash weight="bold" className="w-4 h-4" />;
    if (audio.phase === "recording") return <Stop weight="fill" className="w-4 h-4 text-slate-950" />;
    if (audio.phase === "transcribing") return <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />;
    return <Microphone weight="bold" className="w-4 h-4" />;
  })();

  const micLabel = (() => {
    if (!audio.isSupported) return "Áudio indisponível neste navegador";
    if (audio.phase === "recording") return "Parar e transcrever";
    if (audio.phase === "transcribing") return "Transcrevendo…";
    if (!transcriptionKey.canTranscribe && !transcriptionKey.isLoading) {
      return "Configure uma API key da OpenAI para gravar áudio";
    }
    return "Gravar áudio";
  })();

  const micDisabled =
    !audio.isSupported || isDisabled || audio.phase === "transcribing";

  const hintDisabled = !onRequestHint || isDisabled || !!hintLoading;

  // A2-A11y — sr-only live region for mic state (WCAG 1.4.1 + 4.1.3).
  const micStatusText = (() => {
    if (audio.phase === "recording") return "Áudio: gravando";
    if (audio.phase === "transcribing") return "Áudio: transcrevendo";
    return "Áudio: pronto";
  })();

  return (
    <motion.div
      className="border-t border-border/50 bg-card/60 px-3 py-3"
      initial={composerMotion.initial}
      animate={composerMotion.animate}
    >
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {micStatusText}
      </div>
      <div className="flex items-end gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={micDisabled}
                aria-label={micLabel}
                data-testid="composer-mic"
                onClick={handleMicClick}
                className={cn(
                  audio.phase === "recording" &&
                    "bg-destructive text-slate-950 ring-2 ring-cyan-400 ring-offset-2 ring-offset-background hover:bg-red-400 hover:text-slate-950",
                )}
              >
                {micIcon}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>{micLabel}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={hintDisabled}
                aria-label="Pedir dica do coach"
                data-testid="composer-hint"
                onClick={() => onRequestHint?.()}
              >
                {hintLoading ? (
                  <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                ) : (
                  <Lightbulb weight="bold" className="w-4 h-4" />
                )}
              </Button>
              {typeof hintPenaltyTotal === "number" && hintPenaltyTotal > 0 ? (
                /* A12-A11y — semantic warning token + bumped to 10px for AA
                   contrast on small text (WCAG 1.4.3). */
                <span className="pointer-events-none absolute -right-1 -top-1 rounded-full bg-warning px-1 text-[10px] font-semibold text-warning-foreground">
                  -{hintPenaltyTotal}
                </span>
              ) : null}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {onRequestHint
              ? "Dica do coach — afeta levemente sua nota final"
              : "Dicas indisponíveis"}
          </TooltipContent>
        </Tooltip>

        <Textarea
          ref={taRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKey}
          placeholder={
            audio.phase === "recording"
              ? "Gravando… clique no botão vermelho para parar."
              : "Escreva sua próxima fala…"
          }
          rows={1}
          disabled={isDisabled || audio.phase === "transcribing"}
          className="min-h-[42px] resize-none flex-1"
          data-testid="composer-textarea"
        />

        <Button
          type="button"
          size="icon"
          disabled={!canSend}
          onClick={submit}
          aria-label="Enviar mensagem"
          data-testid="composer-send"
        >
          <PaperPlaneRight weight="bold" className="w-4 h-4" />
        </Button>
      </div>
      <TranscriptionKeyGate.Modal
        open={showKeyModal}
        onClose={() => setShowKeyModal(false)}
      />
    </motion.div>
  );
}
